import React from 'react';
import { snapshotView, snapshotVersion } from '../utils/structuralActionSnapshot';

/**
 * The details block for one structural action log entry.
 *
 * Shared by the Settings page and the reconciliation modal, which previously carried
 * byte-identical copies of this switch. Two copies of a renderer that reads a snapshot
 * format is the same drift problem the backend had: when the format changed, one copy was
 * always going to be updated and the other forgotten.
 *
 * Reads through snapshotView so v1 and v2 entries render the same. Counts for delete and
 * merge come from the v2 captured tables when present — which is also the first time the
 * UI can honestly say how much a revert will restore, because v1 only counted the few
 * tables it happened to snapshot.
 */

/** Row counts from a v2 envelope's captured state. Null for v1, which never had them. */
function capturedCounts(log) {
  if (snapshotVersion(log.original_snapshot) < 2) return null;
  const tables = log.original_snapshot?.before?.tables;
  if (!tables) return null;
  return {
    batches: (tables.InventoryBatches || []).length,
    recipes: (tables.RecipeIngredients || []).length,
    complimentary: (tables.ComplimentaryRecipeIngredients || []).length,
    prepComponents: (tables.PersonalizedDecompositionComponents || []).length,
  };
}

const SubDetail = ({ children, tone }) => (
  <div
    className="log-sub-details-clean"
    style={{ marginTop: '4px', fontSize: '12px', color: tone === 'warn' ? '#e67e22' : '#666' }}
  >
    {children}
  </div>
);

const StructuralActionDetails = ({ log }) => {
  const view = snapshotView(log);
  const orig = log.original_snapshot || {};
  const counts = capturedCounts(log);

  switch (log.action_type) {
    case 'rename':
      return (
        <div className="log-details flex-details">
          <div className="from-side"><strong>From:</strong> {view.oldName}</div>
          <div className="to-side"><strong>To:</strong> {view.newName}</div>
        </div>
      );

    case 'unit_change':
      return (
        <div className="log-details">
          <div className="item-name-row"><strong>Item:</strong> {view.itemName}</div>
          <div className="flex-details">
            <div className="from-side"><strong>From Unit:</strong> {view.oldUnit}</div>
            <div className="to-side">
              <strong>To Unit:</strong> {view.newUnit}{' '}
              <span className="factor-text">(1 = {view.conversionFactor || 1})</span>
            </div>
          </div>
        </div>
      );

    case 'merge':
      return (
        <div className="log-details">
          <div className="flex-details">
            <div className="from-side"><strong>Merge from:</strong> {view.duplicateName}</div>
            <div className="to-side"><strong>Into master:</strong> {view.masterName}</div>
          </div>
          {counts
            // v2 captures BOTH items, so a revert restores the master's quantities too
            // rather than subtracting an addend back out of them.
            ? <SubDetail>Both items captured — {counts.batches} batch(es), {counts.recipes} recipe link(s) restorable</SubDetail>
            : <SubDetail>Stock transferred: {orig.duplicateItem?.current_stock}</SubDetail>}
        </div>
      );

    case 'delete': {
      const batches = counts ? counts.batches : (orig.batches || []).length;
      const recipes = counts ? counts.recipes : (orig.recipeIngredients || []).length;
      const preps = counts ? counts.prepComponents : (orig.prepComponents || []).length;
      const itemName = view.itemName || orig.item?.name;

      return (
        <div className="log-details">
          <div className="item-name-row"><strong>Deleted Item:</strong> {itemName}</div>
          {!counts && (
            <SubDetail>
              Stock deleted: {orig.item?.current_stock} {orig.item?.standard_unit_symbol || ''}
            </SubDetail>
          )}
          {batches > 0 && <SubDetail>Batches deleted: {batches}</SubDetail>}
          {recipes > 0 && <SubDetail tone="warn">Recipe links to restore: {recipes}</SubDetail>}
          {preps > 0 && <SubDetail tone="warn">Preparation links to restore: {preps}</SubDetail>}
          {counts && counts.complimentary > 0 && (
            <SubDetail tone="warn">Complimentary links to restore: {counts.complimentary}</SubDetail>
          )}
        </div>
      );
    }

    case 'convert':
      // The item is the face, the preparation is the engine. Shown in that direction because
      // that is what changed: the recipes did not move, what they consume did.
      return (
        <div className="log-details">
          <div className="flex-details">
            <div className="from-side"><strong>Ingredient:</strong> {view.itemName}</div>
            <div className="to-side"><strong>Now made from:</strong> {view.prepName}</div>
          </div>
          {view.prepYield ? (
            <SubDetail>
              One batch makes {view.prepYield} {view.prepYieldUnit || ''} from {view.componentCount || 0} ingredient(s)
            </SubDetail>
          ) : null}
          {view.recipeCount ? (
            <SubDetail>Affects {view.recipeCount} recipe(s) — they still point at "{view.itemName}"</SubDetail>
          ) : null}
        </div>
      );

    case 'unlink_preparation':
      return (
        <div className="log-details">
          <div className="flex-details">
            <div className="from-side"><strong>Ingredient:</strong> {view.itemName}</div>
            <div className="to-side"><strong>Purchased again</strong></div>
          </div>
          <SubDetail>No longer made from "{view.prepName}" — sales deduct it directly</SubDetail>
        </div>
      );

    case 'revert':
      // Reverts are entries in their own right now, not a flag on the row they undid.
      return (
        <div className="log-details">
          <div className="item-name-row">
            <strong>Undid action</strong> #{view.revertedLogId}
            {orig.reverted_action_type ? ` (${orig.reverted_action_type})` : ''}
          </div>
        </div>
      );

    default:
      return <div>Unknown Action</div>;
  }
};

export default StructuralActionDetails;
